// src/hooks/useWorker.js
import { useState, useCallback, useRef, useEffect } from 'react';

// Task result cache to avoid redundant processing
const resultCache = new Map();
const CACHE_SIZE = 20;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Hook to interact with a Web Worker for heavy computations
 * Includes better error handling, task timeout, improved cleanup,
 * and result caching for improved performance
 * 
 * @param {string} workerPath - Path to the worker file
 * @returns {Object} Worker interface
 */
export const useWorker = (workerPath) => {
  const [isLoading, setIsLoading] = useState({});
  const [error, setError] = useState(null);
  const workerRef = useRef(null);
  const callbacksRef = useRef({});
  const taskIdCounterRef = useRef(0);
  const timeoutsRef = useRef({});
  const taskPriorityRef = useRef({});

  // Cleanup old cache entries
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      resultCache.forEach((value, key) => {
        if (now > value.expiry) {
          resultCache.delete(key);
        }
      });
    }, 60000); // Clean up every minute
    
    return () => clearInterval(cleanupInterval);
  }, []);

  // Initialize worker
  useEffect(() => {
    let isMounted = true;
    
    // Create the worker instance
    try {
      // First check if Worker API is available
      if (typeof Worker === 'undefined') {
        throw new Error('Web Worker API is not supported in this browser');
      }
      
      workerRef.current = new Worker(workerPath);
      
      // Set up message handler
      workerRef.current.onmessage = (e) => {
        if (!isMounted) return;
        
        const { id, result, error: workerError, task, payload } = e.data;
        
        // Call the stored callback with results
        if (callbacksRef.current[id]) {
          // Clear any timeout for this task
          if (timeoutsRef.current[id]) {
            clearTimeout(timeoutsRef.current[id]);
            delete timeoutsRef.current[id];
          }
          
          // Cache successful search results for reuse
          if (!workerError && task === 'search-products' && payload) {
            const cacheKey = getCacheKey(task, payload);
            resultCache.set(cacheKey, {
              result,
              expiry: Date.now() + CACHE_TTL
            });
            
            // Limit cache size
            if (resultCache.size > CACHE_SIZE) {
              const oldestKey = resultCache.keys().next().value;
              resultCache.delete(oldestKey);
            }
          }
          
          if (workerError) {
            callbacksRef.current[id].reject(new Error(workerError));
          } else {
            callbacksRef.current[id].resolve(result);
          }
          
          // Clean up the callback
          delete callbacksRef.current[id];
          delete taskPriorityRef.current[id];
          
          setIsLoading(prev => {
            const updated = { ...prev };
            delete updated[id];
            return updated;
          });
        }
      };
      
      // Improved error handler with better error classification
      workerRef.current.onerror = (err) => {
        if (!isMounted) return;
        
        // Create a more helpful error message
        let errorMsg;
        if (err.message) {
          errorMsg = `Worker error: ${err.message}`;
        } else if (err.type === 'error') {
          errorMsg = 'Web Worker encountered an error';
        } else {
          errorMsg = 'Unknown worker error occurred';
        }
        
        // Only log to console in development
        if (process.env.NODE_ENV === 'development') {
          console.warn('Worker error:', err);
        }
        
        // For UI, we set a cleaner message
        setError(errorMsg);
        
        // If there are too many pending tasks, this might indicate a worker issue
        const pendingTasks = Object.keys(callbacksRef.current).length;
        if (pendingTasks > 5) {
          // Too many pending tasks, let's restart the worker
          console.warn(`Worker appears stuck with ${pendingTasks} pending tasks. Restarting.`);
          if (workerRef.current) {
            workerRef.current.terminate();
            workerRef.current = new Worker(workerPath);
          }
        }
        
        // Reject all pending promises with more informative errors
        Object.entries(callbacksRef.current).forEach(([id, { reject }]) => {
          reject(new Error('Worker failed: ' + errorMsg));
          
          // Clear any timeouts
          if (timeoutsRef.current[id]) {
            clearTimeout(timeoutsRef.current[id]);
            delete timeoutsRef.current[id];
          }
        });
        
        // Reset state
        callbacksRef.current = {};
        taskPriorityRef.current = {};
        setIsLoading({});
      };
    } catch (err) {
      if (!isMounted) return;
      
      setError('Could not initialize worker: ' + err.message);
      console.error('Worker initialization failed:', err);
    }
    
    // Clean up worker when component unmounts
    return () => {
      isMounted = false;
      
      // Clear all timeouts
      Object.values(timeoutsRef.current).forEach(clearTimeout);
      timeoutsRef.current = {};
      
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
    };
  }, [workerPath]);

  // Helper function to generate cache keys
  const getCacheKey = (task, payload) => {
    // For search tasks, only include relevant parts in the cache key
    if (task === 'search-products' && payload) {
      const { term, fields } = payload;
      const productsHash = payload.products ? 
        payload.products.length + '-' + 
        (payload.products.length > 0 ? payload.products[0]._id : '') : 
        '0';
      
      return `${task}-${term}-${fields?.join(',')}-${productsHash}`;
    }
    
    // For other tasks, create a key from task and stringified payload
    return `${task}-${JSON.stringify(payload)}`;
  };

  /**
   * Execute a task in the web worker with timeout and priority
   * @param {string} task - Task name
   * @param {any} payload - Data to process
   * @param {Object} options - Optional configurations
   * @param {number} options.timeout - Timeout in ms (default: 10000)
   * @param {number} options.priority - Task priority (1-5, 5 is highest)
   * @param {boolean} options.useCache - Whether to use cached results (default: true)
   * @returns {Promise} - Promise resolving to the worker result
   */
  const executeTask = useCallback((
    task, 
    payload, 
    { 
      timeout = 10000, 
      priority = 3,
      useCache = true 
    } = {}
  ) => {
    // First check if we have a cached result for this task
    if (useCache && task === 'search-products') {
      const cacheKey = getCacheKey(task, payload);
      const cachedResult = resultCache.get(cacheKey);
      
      if (cachedResult && Date.now() < cachedResult.expiry) {
        console.log('Using cached worker result for:', task);
        return Promise.resolve(cachedResult.result);
      }
    }
    
    if (!workerRef.current) {
      // Try to provide a fallback for browsers without worker support
      if (task === 'search-products' && payload.products && payload.term) {
        const term = payload.term.toLowerCase();
        const fields = payload.fields || ['name', 'description'];
        
        return Promise.resolve(
          payload.products.filter(item => 
            fields.some(field => 
              item[field] && String(item[field]).toLowerCase().includes(term)
            )
          )
        );
      }
      return Promise.reject(new Error('Worker not initialized'));
    }
    
    const id = taskIdCounterRef.current++;
    setIsLoading(prev => ({ ...prev, [id]: true }));
    
    // Store task priority
    taskPriorityRef.current[id] = {
      task,
      priority,
      timestamp: Date.now()
    };
    
    return new Promise((resolve, reject) => {
      // Store callback for when worker responds
      callbacksRef.current[id] = { resolve, reject };
      
      // Set timeout to prevent hanging tasks
      timeoutsRef.current[id] = setTimeout(() => {
        // Reject the promise if worker takes too long
        if (callbacksRef.current[id]) {
          callbacksRef.current[id].reject(new Error(`Worker task ${task} timed out after ${timeout}ms`));
          delete callbacksRef.current[id];
          delete taskPriorityRef.current[id];
          
          setIsLoading(prev => {
            const updated = { ...prev };
            delete updated[id];
            return updated;
          });
        }
        delete timeoutsRef.current[id];
      }, timeout);
      
      // Send message to worker
      try {
        workerRef.current.postMessage({ 
          task, 
          payload, 
          id,
          priority,
          timestamp: Date.now()
        });
      } catch (err) {
        // Handle errors like attempting to clone uncloneable objects
        clearTimeout(timeoutsRef.current[id]);
        delete timeoutsRef.current[id];
        delete callbacksRef.current[id];
        delete taskPriorityRef.current[id];
        
        setIsLoading(prev => {
          const updated = { ...prev };
          delete updated[id];
          return updated;
        });
        
        reject(new Error(`Failed to send data to worker: ${err.message}`));
      }
    });
  }, []);

  /**
   * Get loading state for a specific task type
   * @param {string} taskType - The type of task to check
   * @returns {boolean} True if any task of this type is loading
   */
  const isTaskTypeLoading = useCallback((taskType) => {
    return Object.keys(isLoading).some(id => 
      id.toString().startsWith(`${taskType}_`)
    );
  }, [isLoading]);

  /**
   * Clear the worker's result cache for specific tasks or all tasks
   * @param {string} taskType - Optional task type to clear cache for
   */
  const clearCache = useCallback((taskType = null) => {
    if (taskType) {
      const keysToDelete = [];
      resultCache.forEach((_, key) => {
        if (key.startsWith(taskType)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => resultCache.delete(key));
    } else {
      resultCache.clear();
    }
  }, []);

  /**
   * Terminate current tasks and restart the worker
   * Useful for when the worker gets into a bad state
   */
  const restartWorker = useCallback(() => {
    if (workerRef.current) {
      // Terminate the worker
      workerRef.current.terminate();
      
      // Reject all pending promises
      Object.entries(callbacksRef.current).forEach(([id, { reject }]) => {
        reject(new Error('Worker was restarted'));
        
        // Clear any timeouts
        if (timeoutsRef.current[id]) {
          clearTimeout(timeoutsRef.current[id]);
          delete timeoutsRef.current[id];
        }
      });
      
      // Reset state
      callbacksRef.current = {};
      taskPriorityRef.current = {};
      
      // Create a new worker
      workerRef.current = new Worker(workerPath);
      
      // Reset loading state
      setIsLoading({});
      setError(null);
    }
  }, [workerPath]);

  return {
    executeTask,
    isLoading: Object.values(isLoading).some(Boolean),
    isTaskTypeLoading,
    error,
    resetError: () => setError(null),
    clearCache,
    restartWorker
  };
};

export default useWorker;